// src/components/CashManagement/ChequeModal.jsx

import React, { useState, useEffect } from 'react';
import cashService from '../../services/cashService';

const ChequeModal = ({ isOpen, onClose, onSave, selectedInventory, existingData }) => {
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    inventoryId: '',
    number: '',
    bank: '',
    amount: '',
    expireDate: new Date().toISOString().split('T')[0],
    customerId: ''
  });

  const [chequeList, setChequeList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Load existing draft data when modal opens
  useEffect(() => {
    if (isOpen && existingData) {
      console.log('📋 Loading existing draft cheques:', existingData);
      setChequeList(existingData);
    }
  }, [isOpen, existingData]);

  // Sync selected inventory from parent
  useEffect(() => {
    if (selectedInventory && selectedInventory !== 'ALL') {
      setFormData(prev => ({
        ...prev,
        inventoryId: selectedInventory
      }));
    }
  }, [selectedInventory]);

  // Load customers when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCustomers();
    }
  }, [isOpen]);

  const loadCustomers = async () => {
  try {
    const data = await cashService.getCustomers(); // Service already returns the data
    console.log('👥 Customers API response:', data);
    setCustomers(data);
  } catch (error) {
    console.error('Failed to load customers:', error);
    setCustomers([]);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomerSearch = (e) => {
    setCustomerSearch(e.target.value);
    setShowCustomerDropdown(true);
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({ ...prev, customerId: customer.id }));
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.code?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const getSelectedCustomerName = () => {
    if (!formData.customerId) return '';
    const customer = customers.find(c => c.id === formData.customerId);
    return customer ? customer.name : '';
  };

  const handleAddCheque = (e) => {
    e.preventDefault();

    if (!formData.inventoryId || !formData.number || !formData.bank || !formData.amount || !formData.customerId) {
      alert('Please fill all required fields');
      return;
    }

    const selectedCustomer = customers.find(c => c.id === formData.customerId);

    const newCheque = {
      id: Date.now(), // Temporary ID for frontend tracking
      number: parseInt(formData.number),
      bank: formData.bank,
      amount: parseFloat(formData.amount),
      expireDate: formData.expireDate,
      inventoryId: parseInt(formData.inventoryId),
      customerId: parseInt(formData.customerId),
      // Store customer name for display purposes
      customerName: selectedCustomer ? selectedCustomer.name : ''
    };

    setChequeList(prev => [...prev, newCheque]);
    
    // Reset form but keep inventory
    setFormData({
      inventoryId: formData.inventoryId,
      number: '',
      bank: '',
      amount: '',
      expireDate: new Date().toISOString().split('T')[0],
      customerId: ''
    });
    setCustomerSearch('');
  };

  const handleDeleteCheque = (id) => {
    setChequeList(prev => prev.filter(cheque => cheque.id !== id));
  };

  const handleSaveDraft = () => {
    if (chequeList.length === 0) {
      alert('Please add at least one cheque');
      return;
    }

    console.log('📝 Saving draft cheques:', chequeList);
    
    // Remove customerName before saving (not needed in backend)
    const chequesToSave = chequeList.map(({ customerName, ...cheque }) => cheque);
    
    // Pass draft to parent component
    onSave(chequeList); // Keep customerName for display in parent
    
    // Don't close modal or reset - let parent handle it
  };

  const handleReset = () => {
    setFormData({
      inventoryId: selectedInventory !== 'ALL' ? selectedInventory : '',
      number: '',
      bank: '',
      amount: '',
      expireDate: new Date().toISOString().split('T')[0],
      customerId: ''
    });
    setChequeList([]);
    setCustomerSearch('');
  };

  const handleClose = () => {
    if (isSaving) return;
    
    // If there are unsaved cheques, warn user
    if (chequeList.length > 0 && !existingData) {
      const confirmClose = window.confirm(
        'You have unsaved cheques. Are you sure you want to close?'
      );
      if (!confirmClose) return;
    }
    
    handleReset();
    onClose();
  };

  const getTotalAmount = () => {
    return chequeList.reduce((sum, cheque) => sum + cheque.amount, 0);
  };

  if (!isOpen) return null;

  return (
    <div className="cash-modal-overlay" onClick={handleClose}>
      <div className="cash-modal-content cheque-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="cash-modal-header">
          <h2>Add Cheques</h2>
          <button className="cash-close-btn" onClick={handleClose} disabled={isSaving}>
            &times;
          </button>
        </div>

        <div className="cash-modal-body">
          {/* Cheque Form */}
          <form onSubmit={handleAddCheque}>
            <div className="form-row">
              <div className="form-group">
                <label>Inventory *</label>
                <input
                  type="text"
                  value={formData.inventoryId}
                  disabled
                  className="locked-field"
                />
              </div>

              <div className="form-group">
                <label>Cheque Number *</label>
                <input
                  type="number"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                  placeholder="123456"
                  required
                  disabled={isSaving}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Bank *</label>
                <select
                  name="bank"
                  value={formData.bank}
                  onChange={handleChange}
                  required
                  disabled={isSaving}
                >
                  <option value="">Select Bank</option>
                  <option value="HNB">HNB</option>
                  <option value="BOC">BOC</option>
                  <option value="Sampath">Sampath</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Seylan">Seylan</option>
                  <option value="NDB">NDB</option>
                  <option value="HBL">HBL</option>
                  <option value="HSBC">HSBC</option>
                  <option value="StandardChartered">Standard Chartered</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="50000"
                  step="0.01"
                  min="0"
                  required
                  disabled={isSaving}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Expire Date *</label>
                <input
                  type="date"
                  name="expireDate"
                  value={formData.expireDate}
                  onChange={handleChange}
                  required
                  disabled={isSaving}
                />
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label>Customer *</label>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={handleCustomerSearch}
                  onFocus={() => setShowCustomerDropdown(true)}
                  placeholder="Search customer..."
                  required
                  disabled={isSaving}
                  autoComplete="off"
                />
                {showCustomerDropdown && filteredCustomers.length > 0 && (
                  <div className="customer-dropdown">
                    {filteredCustomers.map(customer => (
                      <div
                        key={customer.id}
                        className="customer-option"
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <div className="customer-name">{customer.name}</div>
                        {customer.code && (
                          <div className="customer-code">{customer.code}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="cash-btn cash-btn-add"
                disabled={isSaving}
              >
                + Add to List
              </button>
            </div>
          </form>

          {/* Cheques List */}
          {chequeList.length > 0 && (
            <div className="cheques-list-container">
              <div className="cheques-list-header">
                <h3>Added Cheques ({chequeList.length})</h3>
                <div className="total-amount">
                  Total: Rs. {getTotalAmount().toLocaleString()}
                </div>
              </div>
              
              <div className="cheques-list">
                <table className="cheques-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cheque No.</th>
                      <th>Bank</th>
                      <th>Customer</th>
                      <th>Expire Date</th>
                      <th>Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chequeList.map((cheque, index) => (
                      <tr key={cheque.id}>
                        <td>{index + 1}</td>
                        <td>
                          <span className="cheque-number-badge">
                            {cheque.number}
                          </span>
                        </td>
                        <td>
                          <span className="bank-badge">
                            {cheque.bank}
                          </span>
                        </td>
                        <td>{cheque.customerName}</td>
                        <td>{cheque.expireDate}</td>
                        <td className="amount-cell">Rs. {cheque.amount.toLocaleString()}</td>
                        <td>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteCheque(cheque.id)}
                            disabled={isSaving}
                            title="Delete cheque"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="cash-modal-footer">
          <button 
            className="cash-btn cash-btn-cancel" 
            onClick={handleClose} 
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            className="cash-btn cash-btn-save" 
            onClick={handleSaveDraft} 
            disabled={isSaving || chequeList.length === 0}
          >
            {isSaving ? 'Saving...' : `Save Draft (${chequeList.length})`}
          </button>
        </div>
      </div>

      <style jsx>{`
        .cheque-modal-large {
          max-width: 900px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .form-actions {
          margin-top: 15px;
          display: flex;
          justify-content: flex-end;
        }

        .cash-btn-add {
          background: #10b981;
          color: white;
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .cash-btn-add:hover:not(:disabled) {
          background: #059669;
        }

        .cash-btn-add:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .customer-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 200px;
          overflow-y: auto;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          z-index: 1000;
          margin-top: 4px;
        }

        .customer-option {
          padding: 10px 12px;
          cursor: pointer;
          border-bottom: 1px solid #f3f4f6;
          transition: background 0.2s;
        }

        .customer-option:hover {
          background: #f3f4f6;
        }

        .customer-option:last-child {
          border-bottom: none;
        }

        .customer-name {
          font-size: 14px;
          color: #1f2937;
          font-weight: 500;
        }

        .customer-code {
          font-size: 12px;
          color: #6b7280;
          margin-top: 2px;
        }

        .cheques-list-container {
          margin-top: 30px;
          border-top: 2px solid #e5e7eb;
          padding-top: 20px;
        }

        .cheques-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .cheques-list-header h3 {
          margin: 0;
          font-size: 18px;
          color: #1f2937;
        }

        .total-amount {
          font-size: 18px;
          font-weight: 600;
          color: #059669;
        }

        .cheques-list {
          overflow-x: auto;
        }

        .cheques-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .cheques-table thead {
          background: #f3f4f6;
        }

        .cheques-table th {
          padding: 12px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .cheques-table td {
          padding: 12px;
          border-top: 1px solid #e5e7eb;
          font-size: 14px;
          color: #374151;
        }

        .cheques-table tbody tr:hover {
          background: #f9fafb;
        }

        .cheque-number-badge {
          display: inline-block;
          padding: 4px 10px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .bank-badge {
          display: inline-block;
          padding: 4px 10px;
          background: #fef3c7;
          color: #92400e;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .amount-cell {
          font-weight: 600;
          color: #059669;
        }

        .delete-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 18px;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .delete-btn:hover:not(:disabled) {
          background: #fee2e2;
        }

        .delete-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .locked-field {
          background: #f3f4f6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ChequeModal;